// components/SkeletonLoader/SkeletonLoader.tsx
import React from 'react';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { SkeletonContainer } from '../SkeletonContainer/SkeletonContainer';

interface SkeletonLoaderProps {
    /**
     * Condition d'affichage du skeleton
     */
    isLoading: boolean;

    /**
     * Contenu à afficher quand isLoading = false
     */
    children: React.ReactNode;

    /**
     * Skeleton à afficher quand isLoading = true
     */
    skeleton: React.ReactNode;

    /**
     * Styles pour le container du skeleton
     * Si fourni, le skeleton sera automatiquement wrappé dans un SkeletonContainer
     */
    containerSx?: SxProps<Theme>;

    /**
     * Largeur du container du skeleton
     * @default '100%'
     */
    containerWidth?: string | number;

    /**
     * Hauteur du container du skeleton
     * @default '100%'
     */
    containerHeight?: string | number;

    /**
     * Centrer le skeleton dans son container
     * @default true
     */
    centered?: boolean;

    /**
     * Condition d'affichage du message d'erreur
     * @default false
     */
    isInError?: boolean;

    /**
     * Contenu à afficher quand isInError = true
     * @default null
     */
    errorChildren?: React.ReactNode;
}

/**
 * Wrapper conditionnel pour les skeletons avec container optionnel
 *
 * @example
 * ```tsx
 * // Avec container automatique
 * <SkeletonLoader
 *   isLoading={loading}
 *   skeleton={<SimplePanelSkeleton width="100%" height="300px" />}
 *   containerSx={{
 *     marginLeft: '-40px',
 *     marginRight: '-40px',
 *     height: '300px'
 *   }}
 * >
 *   <MyComponent />
 * </SkeletonLoader>
 *
 * // Sans container (skeleton brut)
 * <SkeletonLoader
 *   isLoading={loading}
 *   skeleton={<SimplePanelSkeleton />}
 * >
 *   <MyComponent />
 * </SkeletonLoader>
 * ```
 */
export const SkeletonLoader: React.FunctionComponent<SkeletonLoaderProps> = function ({
    isLoading,
    children,
    skeleton,
    containerSx,
    containerWidth = '100%',
    containerHeight = '100%',
    centered = true,
    isInError = false,
    errorChildren = null,
}) {
    if (isInError) {
        if (!errorChildren) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: '50px',
                        height: containerHeight,
                        width: containerWidth,
                    }}
                >
                    <Typography variant="l2dbodym">Une erreur est survenue</Typography>
                </Box>
            );
        }

        return errorChildren;
    }

    if (!isLoading) {
        return children;
    }

    // Si pas de containerSx, retourner le skeleton brut
    if (!containerSx) {
        return skeleton;
    }

    // Sinon, wrapper dans un SkeletonContainer
    return (
        <SkeletonContainer
            sx={containerSx}
            width={containerWidth}
            height={containerHeight}
            centered={centered}
        >
            {skeleton}
        </SkeletonContainer>
    );
};
